import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Button,
  IconButton,
  Alert,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import url from '../../serverUrl';

/**
 * Treasury assignment + proof-of-reserves lock_status, for token
 * create/edit forms. Replaces the old free-text "Treasury Key" field.
 *
 * A token can be backed by MULTIPLE treasuries (multi-select below). Each
 * individual treasury still only ever backs one token at a time - picking
 * a treasury here that's already backing a different token will move it
 * over to this one (surfaced via the save response, not silently).
 *
 * Props:
 *   tokenType - canonical type string the backend registry understands
 *               (e.g. 'SYNTHETICS', 'STABLECOIN', 'DERIVATIVES', 'DAT', ...)
 *   tokenId   - the token's numeric id (nothing renders until this exists,
 *               i.e. the token has already been created/saved once)
 */

const LOCK_STATUS_LABELS = {
  immutability: 'Immutability',
  withdrawal_permissions: 'Withdrawal Permissions',
  backing_freeze_status: 'Backing Freeze Status',
  auditability: 'Auditability'
};

const FALLBACK_LOCK_STATUS_VALUES = {
  immutability: ['immutable', 'governed', 'upgradeable', 'multisig'],
  withdrawal_permissions: [
    'no_withdrawal_allowed',
    'withdrawal_allowed_by_governance',
    'withdrawal_allowed_by_multisig',
    'withdrawal_allowed_by_protocol_rules'
  ],
  backing_freeze_status: ['frozen', 'dynamic', 'algorithmic'],
  auditability: ['public_on_chain', 'private', 'restricted']
};

const emptyLockStatus = () => ({
  immutability: '',
  withdrawal_permissions: '',
  backing_freeze_status: '',
  auditability: ''
});

// Turns an axios error into a specific, actionable message instead of a
// generic "failed to save" - includes which step failed, the HTTP status,
// and whatever detail the server sent back (or "no response" for a
// network/CORS failure that never reached the server at all).
const describeAxiosError = (err, step) => {
  if (err?.response) {
    const detail = err.response.data?.error || err.response.data?.message || JSON.stringify(err.response.data);
    return `Failed while ${step} (HTTP ${err.response.status}): ${detail}`;
  }
  if (err?.request) {
    return `Failed while ${step}: no response from server (network error or CORS) - ${err.message}`;
  }
  return `Failed while ${step}: ${err?.message || 'unknown error'}`;
};

// The treasury API isn't behind auth middleware (see server.js /treasury
// mount for why) - same as every other admin token endpoint in this app.
// We still tag writes with whichever admin uid we can find, purely for the
// linked_by_uid audit column, not for access control.
const getCurrentUid = () => {
  try {
    const stored = localStorage.getItem('hootdex_secretcookie');
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed?.user_id ?? null;
  } catch {
    return null;
  }
};

function TreasuryLockStatusFields({ tokenType, tokenId }) {
  const [treasuries, setTreasuries] = useState([]);
  const [lockStatusValues, setLockStatusValues] = useState(
    FALLBACK_LOCK_STATUS_VALUES
  );
  const [selectedKeys, setSelectedKeys] = useState([]);
  // What the server currently has linked to this token. Removals are
  // persisted immediately against this; additions wait for Save (they need
  // lock_status filled in first).
  const [savedKeys, setSavedKeys] = useState([]);
  // treasury_key -> { immutability, withdrawal_permissions, backing_freeze_status, auditability }
  const [lockStatusByKey, setLockStatusByKey] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [saving, setSaving] = useState(false);

  const applySelection = useCallback((keys, list) => {
    setSelectedKeys(keys);

    setLockStatusByKey((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (next[key]) return; // keep any in-progress edits
        const treasury = list.find((t) => t.treasury_key === key);
        next[key] = {
          immutability: treasury?.immutability || '',
          withdrawal_permissions: treasury?.withdrawal_permissions || '',
          backing_freeze_status: treasury?.backing_freeze_status || '',
          auditability: treasury?.auditability || ''
        };
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!tokenId) return;

    (async () => {
      try {
        const [treasuriesRes, optionsRes, currentRes] = await Promise.all([
          axios.get(`${url}/api/v2/treasury/admin/all`),
          axios
            .get(`${url}/api/v2/treasury/lock-status/options`)
            .catch(() => ({ data: FALLBACK_LOCK_STATUS_VALUES })),
          axios
            .get(`${url}/api/v2/treasury/token/${tokenType}/${tokenId}/treasuries`)
            .catch(() => ({ data: [] }))
        ]);

        const list = treasuriesRes.data || [];
        setTreasuries(list);
        setLockStatusValues(optionsRes.data || FALLBACK_LOCK_STATUS_VALUES);

        const currentKeys = (currentRes.data || []).map((l) => l.treasury_key);
        setSavedKeys(currentKeys);
        applySelection(currentKeys, list);
      } catch (err) {
        const statusCode = err?.response?.status;
        const serverMessage =
          err?.response?.data?.error || err?.response?.data?.message;

        let message = 'Could not load treasury data.';
        if (statusCode === 401 || statusCode === 403) {
          message = `Request rejected (HTTP ${statusCode}) - unexpected, this endpoint shouldn't require auth. Check the server logs.`;
        } else if (statusCode === 404) {
          message =
            'Treasury API not found (404) - the backend may not have the treasury-registry routes deployed yet.';
        } else if (statusCode) {
          message = `Failed to load treasury data (HTTP ${statusCode}): ${
            serverMessage || err.message
          }`;
        } else if (err?.message) {
          message = `Failed to load treasury data: ${err.message}`;
        }

        // eslint-disable-next-line no-console
        console.error('TreasuryLockStatusFields load error:', err);
        setStatus({ type: 'error', message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenType, tokenId]);

  const reloadLinks = async () => {
    const res = await axios.get(
      `${url}/api/v2/treasury/token/${tokenType}/${tokenId}/treasuries`
    );
    const keys = (res.data || []).map((l) => l.treasury_key);
    setSavedKeys(keys);
    return keys;
  };

  // Removing a treasury takes effect immediately - previously it only
  // changed local state, and closing the dialog or using the token's main
  // Save button (which never touches treasuries) silently kept the link.
  const unlinkNow = async (keys) => {
    const persisted = keys.filter((k) => savedKeys.includes(k));
    if (!persisted.length) return;
    setSaving(true);
    try {
      for (const key of persisted) {
        try {
          await axios.post(
            `${url}/api/v2/treasury/unlink`,
            { treasury_key: key, token_type: tokenType, token_id: tokenId },
            { timeout: 20000 }
          );
        } catch (err) {
          // 404 = already not linked; anything else is a real failure.
          if (err?.response?.status !== 404) {
            throw describeAxiosError(err, `removing ${key}`);
          }
        }
      }
      await reloadLinks();
      setStatus({
        type: 'success',
        message: `Removed ${persisted.join(', ')} from this token. It can now be assigned to another token.`
      });
    } catch (err) {
      setStatus({
        type: 'error',
        message: typeof err === 'string' ? err : err?.message || 'Failed to remove treasury.'
      });
      // Put the server's truth back on screen.
      try {
        const keys = await reloadLinks();
        applySelection(keys, treasuries);
      } catch (e) {}
    } finally {
      setSaving(false);
    }
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    const keys = typeof value === 'string' ? value.split(',') : value;
    const dropped = selectedKeys.filter((k) => !keys.includes(k));
    applySelection(keys, treasuries);
    unlinkNow(dropped);
  };

  const handleRemove = (key) => {
    applySelection(
      selectedKeys.filter((k) => k !== key),
      treasuries
    );
    unlinkNow([key]);
  };

  const unsavedAdds = selectedKeys.filter((k) => !savedKeys.includes(k));

  const handleLockStatusChange = (key, field) => (e) => {
    setLockStatusByKey((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || emptyLockStatus()), [field]: e.target.value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus({ type: '', message: '' });

    try {
      const uid = getCurrentUid();

      const missingByKey = selectedKeys.filter((key) => {
        const ls = lockStatusByKey[key] || {};
        return Object.keys(LOCK_STATUS_LABELS).some((field) => !ls[field]);
      });

      if (missingByKey.length) {
        setStatus({
          type: 'error',
          message: `Set all four lock_status fields for: ${missingByKey.join(', ')}.`
        });
        return;
      }

      // Save lock_status for every selected treasury first...
      for (const key of selectedKeys) {
        try {
          await axios.patch(
            `${url}/api/v2/treasury/${key}/lock-status`,
            { ...lockStatusByKey[key], uid },
            { timeout: 20000 }
          );
        } catch (err) {
          throw describeAxiosError(err, `saving lock_status for ${key}`);
        }
      }

      // ...then reconcile the token's full backing set in one call.
      let res;
      try {
        res = await axios.post(
          `${url}/api/v2/treasury/sync`,
          {
            token_type: tokenType,
            token_id: tokenId,
            treasury_keys: selectedKeys,
            uid
          },
          { timeout: 20000 }
        );
      } catch (err) {
        throw describeAxiosError(err, 'syncing treasuries to token');
      }

      try {
        await reloadLinks();
      } catch (e) {}

      const stolenNotes = (res.data?.linked || [])
        .filter((l) => l.stolenFromToken)
        .map(
          (l) =>
            `moved from ${l.stolenFromToken.token_type} #${l.stolenFromToken.token_id}`
        );

      setStatus({
        type: 'success',
        message:
          `Treasuries saved (${selectedKeys.length} backing this token).` +
          (stolenNotes.length ? ` Note: ${stolenNotes.join('; ')}.` : '')
      });
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.response?.data?.error || err?.message || 'Failed to save treasury assignment.';

      // eslint-disable-next-line no-console
      console.error('TreasuryLockStatusFields save error:', err);
      setStatus({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  if (!tokenId) {
    return (
      <Typography variant="caption" color="text.secondary">
        Save this token once before assigning a treasury.
      </Typography>
    );
  }

  return (
    <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 2, mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Treasury Backing
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel id="treasury-select-label">Treasuries</InputLabel>
        <Select
          labelId="treasury-select-label"
          label="Treasuries"
          multiple
          value={selectedKeys}
          onChange={handleSelectChange}
          MenuProps={{ disablePortal: true }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((key) => (
                <Chip key={key} label={key} size="small" />
              ))}
            </Box>
          )}
        >
          {treasuries.map((t) => (
            <MenuItem key={t.treasury_key} value={t.treasury_key}>
              {t.treasury_key} ({t.symbol || 'unknown'} - {t.amount})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedKeys.map((key) => {
        const treasury = treasuries.find((t) => t.treasury_key === key);
        const lockStatus = lockStatusByKey[key] || emptyLockStatus();

        return (
          <Box key={key} sx={{ border: '1px dashed #999', borderRadius: 1, p: 1.5, mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600}>
                {key} {treasury ? `(${treasury.symbol || 'unknown'} - ${treasury.amount})` : ''}
              </Typography>
              <IconButton size="small" onClick={() => handleRemove(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {Object.keys(LOCK_STATUS_LABELS).map((field) => (
              <FormControl fullWidth margin="dense" size="small" key={field}>
                <InputLabel id={`${key}-${field}-label`}>
                  {LOCK_STATUS_LABELS[field]}
                </InputLabel>
                <Select
                  labelId={`${key}-${field}-label`}
                  label={LOCK_STATUS_LABELS[field]}
                  value={lockStatus[field]}
                  onChange={handleLockStatusChange(key, field)}
                  MenuProps={{ disablePortal: true }}
                >
                  {(lockStatusValues[field] || []).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        );
      })}

      {selectedKeys.length > 0 && <Divider sx={{ mt: 2 }} />}

      {unsavedAdds.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
          Not saved yet: {unsavedAdds.join(', ')}. Fill in the lock status and
          click "Save Treasury Assignment" - the token's main Save button does
          not save treasuries.
        </Alert>
      )}

      {status.message && (
        <Alert severity={status.type === 'error' ? 'error' : 'success'} sx={{ mt: 1, mb: 1 }}>
          {status.message}
        </Alert>
      )}

      <Button
        variant="outlined"
        size="small"
        disabled={saving}
        onClick={handleSave}
        sx={{ mt: 1 }}
      >
        {saving ? 'Saving...' : 'Save Treasury Assignment'}
      </Button>
    </Box>
  );
}

export default TreasuryLockStatusFields;
